import React, { useState } from 'react';
import {
  AnalysisBlocks, Change, EvidenceList, SkeletonCards, formatKst, useAnalysisRequest,
} from './marketParts';

// 로컬 검증 때만 REACT_APP_NASDAQ_API_URL 로 바꾼다. 비워 두면 운영 주소.
const NASDAQ_API_URL = process.env.REACT_APP_NASDAQ_API_URL
  || 'https://9x834oeyfh.execute-api.ap-northeast-2.amazonaws.com/nasdaq/genai-nasdaq-analisys';

const RANGES = [
  { id: 'first_5', label: '거래대금 1위 ~ 5위' },
  { id: 'last_5', label: '거래대금 6위 ~ 10위' },
];

const formatUsd = (v) => (v == null ? '-' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// 1억 달러 단위. 1조 달러를 넘으면 조로 적는다(시가총액).
const formatUsdKo = (v) => {
  if (!v) return '-';
  if (v >= 1e12) return `${(v / 1e12).toLocaleString('ko-KR', { maximumFractionDigits: 2 })}조 달러`;
  return `${(v / 1e8).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}억 달러`;
};

const STATUS_CLASS = { open: 'live', pre: 'soon', after: 'soon', closed: 'closed' };

const StockCard = ({ stock }) => {
  const idPrefix = `nasdaq-${stock.symbol}`;
  return (
    <article className="paper-card mkt-card">
      <div className="paper-rank" aria-hidden="true">{String(stock.rank).padStart(2, '0')}</div>
      <div className="paper-body">
        <h3 className="paper-title mkt-title">
          {stock.name_ko || stock.name} <span className="mkt-symbol">{stock.symbol}</span>
        </h3>
        {stock.name_ko && <p className="mkt-subname">{stock.name}</p>}
        {stock.headline && <p className="paper-headline">{stock.headline}</p>}

        <div className="mkt-price-row">
          <span className="mkt-price">{formatUsd(stock.price)}</span>
          <ul className="mkt-changes">
            <Change label="전일 대비" value={stock.change_1d} />
            <Change label="5거래일" value={stock.change_5d} />
            <Change label="20거래일" value={stock.change_20d} />
          </ul>
        </div>

        <ul className="paper-meta">
          <li>거래대금 {formatUsdKo(stock.dollar_volume)}</li>
          {stock.volume_ratio != null && <li>거래량 20일 평균의 {stock.volume_ratio.toFixed(1)}배</li>}
          {stock.market_cap ? <li>시가총액 {formatUsdKo(stock.market_cap)}</li> : null}
          {stock.sector && <li>{stock.sector}</li>}
          {stock.insider_filings > 0 && <li className="mkt-flag muted">최근 14일 내부자 거래 공시 {stock.insider_filings}건</li>}
          {stock.headline && !stock.news_explains && <li className="mkt-flag muted">뚜렷한 뉴스 근거 없음</li>}
        </ul>

        <AnalysisBlocks item={stock} idPrefix={idPrefix} />
        <EvidenceList articles={stock.articles} idPrefix={idPrefix} />

        <details className="paper-details">
          <summary>지표 더 보기</summary>
          <ul className="mkt-more">
            {stock.volatility_20d != null && <li>20일 일간 변동성 {stock.volatility_20d.toFixed(2)}%</li>}
            {stock.ma20_gap != null && <li>20일 평균가 대비 {stock.ma20_gap > 0 ? '+' : ''}{stock.ma20_gap.toFixed(2)}%</li>}
            {stock.range_pos != null && (
              <li>최근 3개월 범위 안 위치 {stock.range_pos}% (저가 {formatUsd(stock.low_3m)}, 고가 {formatUsd(stock.high_3m)})</li>
            )}
            {stock.industry && <li>업종 {stock.industry}</li>}
          </ul>
        </details>
      </div>
    </article>
  );
};

const NasdaqSection = () => {
  const [range, setRange] = useState('');
  const { status, data, error, load } = useAnalysisRequest(NASDAQ_API_URL);

  const pick = (id) => {
    setRange(id);
    load({ rank_range: id });
  };

  const stocks = data?.stocks || [];
  const rangeLabel = RANGES.find((r) => r.id === data?.rank_range)?.label;

  return (
    <section className="service-section">
      <h2>나스닥 분석 AI 서비스</h2>

      <fieldset className="field-group">
        <legend>나스닥 100 순위</legend>
        <div className="button-container one-line">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`category-btn ${range === r.id ? 'selected' : ''}`}
              aria-pressed={range === r.id}
              onClick={() => pick(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="results paper-results" aria-live="polite" aria-busy={status === 'loading'}>
        {status === 'idle' && <p className="paper-placeholder">순위를 고르면 바로 분석을 시작합니다.</p>}

        {status === 'loading' && (
          <>
            <p className="paper-placeholder">나스닥 시세와 SEC 공시, 최근 기사를 모아 분석하고 있습니다. 20초 안팎 걸립니다.</p>
            <SkeletonCards />
          </>
        )}

        {status === 'error' && (
          <div className="paper-error">
            <p>{error}</p>
            <button type="button" className="category-btn" onClick={() => load({ rank_range: range })}>다시 시도</button>
          </div>
        )}

        {status === 'done' && data && (
          <>
            <div className="paper-header">
              <h3>나스닥 100 {rangeLabel} 분석</h3>
              <ul className="paper-chips">
                <li>{formatKst(data.generated_at)} 기준</li>
                {data.market_status_label && (
                  <li className={`mkt-status ${STATUS_CLASS[data.market_status] || ''}`}>미국 {data.market_status_label}</li>
                )}
                {data.cached && <li>{Math.max(1, Math.round((data.age_seconds || 0) / 60))}분 전 분석</li>}
              </ul>
              {data.overview && <p className="paper-overview">{data.overview}</p>}
              {!data.explained && (
                <p className="paper-notice">AI 분석을 만들지 못해 시세와 공시, 근거 기사만 보여 드립니다.</p>
              )}
            </div>

            <div className="paper-list">
              {stocks.map((s) => <StockCard key={s.symbol} stock={s} />)}
            </div>

            {data.disclaimer && <p className="mkt-disclaimer">{data.disclaimer}</p>}
          </>
        )}
      </div>
    </section>
  );
};

export default NasdaqSection;
